import { type ReactNode, useCallback, useMemo } from 'react'

import { format } from 'date-fns'
import { FormattedMessage, useIntl } from 'react-intl'
import { toast } from 'react-toastify'

import type { ContractDto } from '@b-stock/contract-api-client'
import { useAccessToken } from '@b-stock/fe-core/bstock-next/components'
import { useFlag } from '@b-stock/fe-core/bstock-next/helpers'
import { isAxiosError } from '@b-stock/fe-core/bstock-next/mutations'
import { type OfferingItem } from '@b-stock/fe-core/bstock-next/queries'
import {
  useIsPast,
  DateTimeFormatType,
  showSnackbar,
  Link,
} from '@b-stock/fe-core/bstock-react'
import { CommonStrings } from '@b-stock/fe-core/locale'
import {
  type OfferDto,
  type QuantityDetailsDto,
  type CreateQuantityDetailsDto,
  type OfferingDto,
} from '@b-stock/offering-api-client'

import { useIsSellerUserOfferingOwner } from '@/buyer-portal/components/features/listing/useIsSellerUserOfferingOwner'
import useAccountId from '@/buyer-portal/helpers/useAccountId'
import { useIsServiceAccountOnly } from '@/buyer-portal/helpers/useIsServiceAccountOnly'
import { createOffer } from '@/buyer-portal/mutations/offers/createOffer'
import { updateOffer } from '@/buyer-portal/mutations/offers/updateOffer'
import { FeatureFlags } from '@/helpers/featureFlags'
import { myAccountSettingsBase } from '@/helpers/myAccountNav'
import { useGaSessionIdPayload } from '@/helpers/telemetry/useGaSessionIdPayload'

import type { OfferingFormData, OfferingItemRow } from './types'
import { useUpdateOffersQueryData } from './useUpdateOffersQueryData'

const ns = 'Offerings.Details'

const NA = <CommonStrings.NotAvailableAbbr.Component />

export const useOfferingForm = ({
  offering,
  offeringItems,
  offers,
  contract,
  enableEditing,
  setEnableEditing,
}: {
  offeringItems: OfferingItem[]
  offers: OfferDto[]
  offering: OfferingDto
  contract?: ContractDto | null
  enableEditing: boolean
  setEnableEditing: (enableEditing: boolean) => void
}) => {
  const { formatMessage } = useIntl()
  const accessToken = useAccessToken({ required: true })
  const buyerAccountId = useAccountId()
  const myAccountHpNav = useFlag(FeatureFlags.MY_ACCOUNT_HP_NAV)
  const {
    _id: offeringId,
    isWinnerTakeAll,
    listingId,
    listingStatus,
    offerAmountUnits,
    offerEndTime,
  } = offering

  const offerId = offers.find(
    (offer) => offer.accountId === buyerAccountId
  )?._id

  const offerMap: Record<string, QuantityDetailsDto | undefined> = {}
  offers.forEach((offer) =>
    (offer.offer.quantities || []).forEach((o) => (offerMap[o.itemId] = o))
  )

  const isOfferingSeller = useIsSellerUserOfferingOwner({ offering })
  const isServiceAccountOnly = useIsServiceAccountOnly()

  const isActionsDisabledUser = isOfferingSeller || isServiceAccountOnly

  const offerEndTimeDate = useMemo(
    () => (offerEndTime ? new Date(offerEndTime) : null),
    [offerEndTime]
  )

  const isOfferLive =
    !useIsPast(offerEndTimeDate) && listingStatus === 'SCHEDULED'

  const itemRowsDisabled = useMemo(
    () =>
      !isOfferLive ||
      isActionsDisabledUser ||
      (offers.length > 0 && !enableEditing),
    [enableEditing, isActionsDisabledUser, offers.length, isOfferLive]
  )

  const initialValues: OfferingFormData = {
    contract,
    isWinnerTakeAll,
    itemRows: offeringItems.map((o) => {
      const offerStartPrice = o.customAttributes?.offerStartPrice ?? null
      return {
        disabled: itemRowsDisabled,
        isWinnerTakeAll,
        item: o,
        offerAmount: isActionsDisabledUser
          ? null
          : (offerMap[o._id]?.amount ?? null),
        offerAmountUnits,
        offerQuantity: isActionsDisabledUser
          ? null
          : (offerMap[o._id]?.quantityMax ?? null),
        offerStartPrice,
      }
    }),
    offerAmountUnits,
  }

  const getQuantities = (
    itemRows: OfferingItemRow[],
    contract?: ContractDto | null
  ): CreateQuantityDetailsDto[] => {
    return itemRows.reduce<CreateQuantityDetailsDto[]>((acc, o) => {
      if (contract && typeof o.offerAmount === 'number') {
        acc.push({
          itemId: o.item._id,
          amount: o.offerAmount,
          quantityMax: o.item.quantity,
        })
      } else if (
        typeof o.offerAmount === 'number' &&
        typeof o.offerQuantity === 'number'
      ) {
        acc.push({
          itemId: o.item._id,
          amount: o.offerAmount,
          quantityMax: o.offerQuantity,
        })
      }
      return acc
    }, [])
  }

  const updateOffersQueryData = useUpdateOffersQueryData()
  // GLOB-4954: `{}` unless the kill switch is on AND the GA cookies are present.
  const getGaSessionIdPayload = useGaSessionIdPayload()

  const onSubmit = useCallback(
    async (values: OfferingFormData) => {
      const quantities = getQuantities(values.itemRows, contract)

      if (quantities.length === 0 && values.itemRows.length > 1) {
        showSnackbar({
          content: formatMessage({ id: `${ns}.form.errors.emptyForm` }),
          options: { type: 'error', autoClose: true },
        })
      }

      if (!quantities.length || !offeringId) return

      const gaSessionIdPayload = getGaSessionIdPayload()

      try {
        if (offerId) {
          const updatedOfferResponse = await updateOffer({
            accessToken,
            offerId,
            replaceOfferDto: {
              offer: {
                quantities,
              },
            },
            gaSessionIdPayload,
          })

          updateOffersQueryData({
            accessToken,
            listingId,
            offeringId,
            offers: [updatedOfferResponse.data],
          })
        } else {
          const createdOfferResponse = await createOffer({
            accessToken,
            createOfferDto: {
              offeringId,
              offer: {
                quantities,
              },
            },
            gaSessionIdPayload,
          })

          updateOffersQueryData({
            accessToken,
            listingId,
            offeringId,
            offers: [createdOfferResponse.data],
          })
        }
      } catch (error) {
        if (isAxiosError(error)) {
          const response = error.response

          if (
            response?.status === 406 &&
            (response.data as { errorMessage?: string }).errorMessage ===
              'Offering has been canceled'
          ) {
            showSnackbar({
              content: formatMessage(
                { id: `${ns}.form.errorSnackbar.canceledOffering` },
                { type: offerId ? 'updated' : 'made' } // TODO localize
              ),
              options: { type: 'error', autoClose: true },
            })
            return
          }

          if (
            response?.status === 406 &&
            (response.data as { errorMessage?: string }).errorMessage ===
              'Offering has been completed'
          ) {
            showSnackbar({
              content: formatMessage(
                { id: `${ns}.form.errorSnackbar.acceptedEarly` },
                {
                  date: offerEndTime
                    ? format(offerEndTime, DateTimeFormatType.FULL)
                    : NA,
                }
              ),
              options: { type: 'error', autoClose: true },
            })
            return
          }

          if (
            response?.status === 400 &&
            (response.data as { errorMessage?: string }).errorMessage ===
              'This buyer does not have permissions to make offer'
          ) {
            showSnackbar({
              content: (
                <span>
                  <FormattedMessage
                    id={`${ns}.offeringStatusBanner.noPermissions.message`}
                    values={{
                      link: (content: ReactNode) => (
                        <Link
                          href={`${myAccountSettingsBase(myAccountHpNav)}/business-profile`}
                        >
                          {content}
                        </Link>
                      ),
                    }}
                  />
                </span>
              ),
              options: { type: 'error', autoClose: 'action' },
            })
            return
          }
        }

        showSnackbar({
          content: formatMessage({ id: 'Common.error.unexpectedAPIError' }),
          options: { type: 'error', autoClose: true },
        })
        return
      } finally {
        setEnableEditing(false)
      }

      toast.dismiss() // dismiss any existing toast
      showSnackbar({
        content: formatMessage({
          id: `${ns}.form.ctaSuccess.${offerId ? 'updated' : 'created'}`,
        }),
        options: {
          type: 'success',
          autoClose: true,
          toastId: crypto.randomUUID(), // no control of validation toastId, so using random UUID to differentiate
        },
      })
    },
    [
      accessToken,
      contract,
      formatMessage,
      getGaSessionIdPayload,
      listingId,
      myAccountHpNav,
      offerEndTime,
      offerId,
      offeringId,
      setEnableEditing,
      updateOffersQueryData,
    ]
  )

  return {
    initialValues,
    isEnabled: !isActionsDisabledUser && isOfferLive,
    onSubmit,
  }
}
